import { Collapse } from '@mui/material';
import { useGenders } from '@starsoft/common/api/gender';
import {
  DatePicker,
  FormGroup,
  Input,
  SelectInput,
} from '@starsoft/common/components';
import {
  CreateKycInputDto,
  FetchUserIdentitiesValidateInputDto,
} from '@starsoft/common/dto';
import { KycIdentityOriginIdType } from '@starsoft/common/enums/kyc/identity/origin';
import {
  useKycInformationHandler,
  useTranslation,
} from '@starsoft/common/hooks';
import { Either } from '@starsoft/common/interfaces';
import { Gender } from '@starsoft/common/models/gender';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { KycIdentityOriginId } from '../../../../../../enums';
import { UserIdentityFormFields } from '../../../../../../enums/user-identity/form-fields';
import { useUserIdentityLookUpHandler } from '../../../../../../hooks/user-identity/useUserIdentityLookUpHandler';
import { useUserOriginId } from '../../../../../../hooks/user-identity/useUserOriginId';
import styles from '../styles.module.scss';
import KycUserIdentityFormPersolanIdentityLookUpNameInputs from './Inputs/Name';
import { UserIdentitiesFormContentPersonalIdentityLookUpProps } from './props';

/**
 * UserIdentitiesFormContentPersonalIdentityLookUp Component
 * Renders a form for personal identity lookup with fields for tax ID, name, gender and birthdate.
 * The form adapts based on the user's origin country and handles validation.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isSkeleton - Whether to show skeleton loading state
 * @param {boolean} props.isContainerColor - Whether to use container background color
 * @param {boolean} props.readonly - Whether form fields should be read-only
 * @param {string} props.externalOriginId - External origin ID to override user's origin
 * @returns {JSX.Element} Personal identity lookup form
 *
 * @example
 * <UserIdentitiesFormContentPersonalIdentityLookUp
 *   isSkeleton={false}
 *   isContainerColor={true}
 *   readonly={false}
 *   externalOriginId="BR"
 * />
 */
export default function UserIdentitiesFormContentPersonalIdentityLookUp({
  isSkeleton,
  isContainerColor,
  readonly,
  externalOriginId,
  minimalForm = false,
}: UserIdentitiesFormContentPersonalIdentityLookUpProps) {
  const { t } = useTranslation('common');
  const originId = useUserOriginId() ?? externalOriginId;
  const methods = useFormContext<
    FetchUserIdentitiesValidateInputDto & CreateKycInputDto
  >();

  const {
    register,
    formState: { dirtyFields },
    setValue,
    watch,
  } = methods;
  const birthdate: Either<string, undefined> = watch('identity.birthDate');
  const genderId: Either<string, undefined> = watch('identity.genderId');

  const {
    useKycMeByOriginIdQuery: { kyc },
  } = useKycInformationHandler({
    externalOriginId: externalOriginId as unknown as KycIdentityOriginIdType,
    externalEnabled: !minimalForm,
  });

  const {
    data: genders,
    error: gendersError,
    refetch: refetchGenders,
    isPending: isPendingGenders,
  } = useGenders();

  /**
   * Sets the gender ID in the form
   * @param {Gender} value - Gender object containing ID to set
   * @example
   * setGenderId({ id: "1", name: "Male" })
   */
  function setGenderId(value: Gender) {
    setValue('identity.genderId', value?.id.toString(), {
      shouldDirty: true,
    });
  }

  const {
    collapseIn,
    isValidLookup,
    useUserIdentityValidateByOriginIdQuery: {
      isLoading,
      errorFieldsMap,
      error,
    },
  } = useUserIdentityLookUpHandler({
    methods,
    isSkeleton,
    readonly,
    externalOriginId,
  });

  /**
   * Sets the birthdate in the form
   * @param {string} value - Date string to set
   * @example
   * setDate("1990-01-01")
   */
  function setDate(value: string) {
    setValue('identity.birthDate', value, {
      shouldDirty: true,
    });
  }

  /**
   * Gets the tax ID field name based on origin country
   * @returns {string} Localized tax ID field name
   * @example
   * // For Brazil
   * getTaxIdNameByOriginId() // Returns "CPF"
   */
  function getTaxIdNameByOriginId(): string {
    switch (originId?.toString()) {
      case KycIdentityOriginId.Brazil?.toString():
        return 'CPF';
      case KycIdentityOriginId.Argentina?.toString():
        return 'CUTI';
      case KycIdentityOriginId.India?.toString():
        return 'PAN';
      case KycIdentityOriginId.Mexico?.toString():
        return 'RFC';
      case KycIdentityOriginId.Indonesia?.toString():
        return 'NPWP';
      case KycIdentityOriginId.Peru?.toString():
        return 'RUC';
      case KycIdentityOriginId.SouthAfrica?.toString():
        return 'ID';
      case KycIdentityOriginId.Nigeria?.toString():
        return 'NIN';
      case KycIdentityOriginId.Singapore?.toString():
        return 'NRIC';
    }
  }

  /**
   * Resets form with KYC identity data if available
   */
  function handleResetForm() {
    if (!kyc || !kyc?.identity) {
      return;
    }

    setValue('identity', {
      ...kyc?.identity,
      originId: kyc?.origin?.id.toString(),
      genderId:
        kyc?.identity?.gender?.id.toString() ??
        kyc?.identity?.genderId?.toString(),
    });
    setValue('identityId', kyc?.identity?.id.toString());
  }

  useEffect(handleResetForm, [kyc, setValue]);

  return (
    <>
      <FormGroup
        label={t(getTaxIdNameByOriginId())}
        loading={{
          label: true,
          secondary: !isContainerColor,
        }}
        isLoading={isSkeleton}
      >
        <Input
          className={isValidLookup && !readonly ? styles.container__input : ''}
          placeholder={t(getTaxIdNameByOriginId())}
          name="identity.identifier"
          register={register}
          isContainerColor={isContainerColor}
          isError={
            (errorFieldsMap?.has(UserIdentityFormFields.TaxId) || !!error) &&
            dirtyFields?.identity?.identifier
          }
          errorMessage={
            errorFieldsMap?.get(UserIdentityFormFields.TaxId)?.length > 0
              ? errorFieldsMap?.get(UserIdentityFormFields.TaxId)
              : t('invalid_document_type')
          }
          showSpinner={isLoading}
          isLoading={isSkeleton}
          readOnly={readonly}
          rightIcon={
            isValidLookup && !readonly ? 'fa-solid fa-circle-check' : undefined
          }
        />
      </FormGroup>
      <Collapse in={collapseIn || minimalForm} className={styles.container}>
        <>
          <div className={styles.container__row}>
            <KycUserIdentityFormPersolanIdentityLookUpNameInputs
              isContainerColor={isContainerColor}
              isSkeleton={isSkeleton}
              readonly={readonly}
              externalOriginId={externalOriginId}
            />
          </div>
          {!minimalForm && (
            <>
              <div className={styles.container__spacer} />
              <FormGroup
                label={t('gender_label')}
                error={gendersError}
                refetch={refetchGenders}
                isLoading={isSkeleton || isPendingGenders}
                loading={{
                  label: true,
                  secondary: !isContainerColor,
                }}
              >
                <SelectInput
                  options={genders?.data as Gender[]}
                  value={genderId}
                  getOptionLabel={option => t(`${option?.id}_gender_label`)}
                  getOptionValue={option => option?.id?.toString()}
                  setValue={setGenderId}
                  disabled={originId == KycIdentityOriginId.Brazil || readonly}
                  loading={isPendingGenders}
                  cardBg={isContainerColor}
                />
              </FormGroup>
              <div className={styles.container__spacer} />
              <DatePicker
                value={birthdate}
                placeholder={t('birthdate_label')}
                showSpinner={isLoading}
                isContainerColor={isContainerColor}
                onChange={setDate}
                isError={
                  errorFieldsMap?.has(UserIdentityFormFields.BirthDate) &&
                  !!dirtyFields?.identity?.birthDate
                }
                errorMessage={
                  errorFieldsMap?.get(UserIdentityFormFields.BirthDate) ??
                  t('birthdate_invalid')
                }
                isLoading={isSkeleton}
                readOnly={originId == KycIdentityOriginId.Brazil || readonly}
              />
            </>
          )}
        </>
      </Collapse>
    </>
  );
}
